import { useState, useEffect, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical, Plus, X, Mail, Clock, CalendarClock } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────

type Tag = {
  id: number; name: string; category: string; color: string
}

type PersonLite = { id: number; full_name: string; personal_email?: string }

type CandidateProfile = {
  id: number
  person: number
  person_name: string
  person_email: string
  person_mobile: string
  person_headline: string
  person_current_title: string
  person_current_company: string
  person_linkedin_url: string
  person_country_code: string
  recruitment_source: string
  recruitment_owner: number | null
  owner_name: string | null
  talent_pool_status: 'NONE' | 'SILVER' | 'GOLD'
  is_archived: boolean
  summary: string
  availability: string
  desired_salary: string | null
  desired_currency: string
  notes_recruitment: string
  tags: number[]
  tags_detail: Tag[]
  last_touch_at: string | null
  next_followup_at: string | null
  consent_at: string | null
  retention_until: string | null
  open_applications_count: number
  total_applications_count: number
  last_application_at: string | null
  hired: boolean
}

type ApplicationLite = {
  id: number
  person: number
  job: number
  job_code: string
  job_title: string
  stage: number
  stage_name: string
  stage_is_won: boolean
  stage_is_lost: boolean
  status: 'ACTIVE' | 'HIRED' | 'REJECTED' | 'WITHDRAWN'
  source: string
  applied_at: string
  resume_url: string
}

type JobLite = {
  id: number
  code: string
  title: string
  status: string
  pipeline: number | null
}

type Activity = {
  id: number
  application: number
  type: string
  body: string
  performed_by_username: string | null
  automatic: boolean
  timestamp: string
}

const TALENT_COLOR: Record<CandidateProfile['talent_pool_status'], string> = {
  NONE: 'bg-gray-100 text-gray-700',
  SILVER: 'bg-slate-200 text-slate-800',
  GOLD: 'bg-amber-100 text-amber-800',
}
const TALENT_LABEL: Record<CandidateProfile['talent_pool_status'], string> = {
  NONE: 'Standard', SILVER: 'Silver', GOLD: 'Gold',
}

const TAG_CATEGORY_COLOR: Record<string, string> = {
  SKILL: 'bg-violet-100 text-violet-700',
  SENIORITY: 'bg-purple-100 text-purple-700',
  LOCATION: 'bg-emerald-100 text-emerald-700',
  LANGUAGE: 'bg-cyan-100 text-cyan-700',
  DEPARTMENT: 'bg-orange-100 text-orange-700',
  OTHER: 'bg-gray-100 text-gray-700',
  '': 'bg-gray-100 text-gray-600',
}

const APP_STATUS_COLOR: Record<ApplicationLite['status'], string> = {
  ACTIVE: 'bg-violet-100 text-violet-700',
  HIRED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
  WITHDRAWN: 'bg-gray-100 text-gray-700',
}

const ACTIVITY_COLOR: Record<string, string> = {
  CREATED: 'bg-slate-100 text-slate-700',
  NOTE: 'bg-violet-100 text-violet-700',
  STAGE_CHANGED: 'bg-indigo-100 text-indigo-700',
  STATUS_CHANGED: 'bg-violet-100 text-violet-700',
  INTERVIEW: 'bg-amber-100 text-amber-700',
  SCORECARD: 'bg-amber-100 text-amber-700',
  OFFER: 'bg-cyan-100 text-cyan-700',
  HIRED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
}

type TabKey = 'profile' | 'applications' | 'activity' | 'notes' | 'files'

// ─────────────────── Page ───────────────────

export default function ATSCandidateDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const candidateId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const candQ = useQuery({
    queryKey: ['ats', 'candidate', candidateId],
    queryFn: async () => (await api.get<CandidateProfile>(`/ats/candidates/${candidateId}/`)).data,
    enabled: !!candidateId,
  })
  const tagsQ = useQuery({
    queryKey: ['ats', 'tags'],
    queryFn: async () => (await api.get<Tag[]>('/ats/tags/')).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<PersonLite[]>('/hr/persons/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<CandidateProfile>) =>
      (await api.patch(`/ats/candidates/${candidateId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ats', 'candidate', candidateId] })
      qc.invalidateQueries({ queryKey: ['ats', 'candidates'] })
    },
  })

  const updatePersonMut = useMutation({
    mutationFn: async ({ personId, payload }: { personId: number; payload: any }) =>
      (await api.patch(`/hr/persons/${personId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ats', 'candidate', candidateId] })
      qc.invalidateQueries({ queryKey: ['hr', 'persons'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/ats/candidates/${candidateId}/`),
    onSuccess: () => {
      toast.success('Candidate profile removed')
      navigate('/admin/ats/candidates')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const logTouchMut = useMutation({
    mutationFn: async (payload: { kind: string; note: string; next_followup: string | null }) =>
      (await api.post(`/ats/candidates/${candidateId}/log-touch/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ats', 'candidate', candidateId] })
      qc.invalidateQueries({ queryKey: ['ats', 'candidates'] })
      qc.invalidateQueries({ queryKey: ['ats', 'activities'] })
      toast.success('Touch logged')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('profile')
  const [considerOpen, setConsiderOpen] = useState(false)
  const [logTouchOpen, setLogTouchOpen] = useState(false)

  // Page-level queries that the header button + ApplicationsTab both rely on.
  // React-query dedupes by key so this is a single network call.
  const appsForCandQ = useQuery({
    queryKey: ['ats', 'applications', { person: candidateId && candQ.data ? candQ.data.person : 0 }],
    queryFn: async () =>
      (await api.get<ApplicationLite[]>(`/ats/applications/?person=${candQ.data!.person}`)).data,
    enabled: !!candQ.data,
  })
  const jobsListQ = useQuery({
    queryKey: ['ats', 'jobs'],
    queryFn: async () => (await api.get<JobLite[]>('/ats/jobs/')).data,
  })

  if (candQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (candQ.isError || !candQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Candidate not found.</p>
          <Link to="/admin/ats/candidates" className="text-violet-600 hover:underline">← Back to candidates</Link>
        </div>
      </AdminLayout>
    )
  }

  const cand = candQ.data
  const allTags = tagsQ.data || []
  const persons = personsQ.data || []

  // Header "Add to job" pulls from the same source ApplicationsTab uses,
  // so the eligibility check is consistent across both entry points.
  const apps = appsForCandQ.data || []
  const allJobs = jobsListQ.data || []
  const appliedJobIds = new Set(apps.map((a) => a.job))
  const eligibleJobs = allJobs.filter((j) => j.status === 'OPEN' && !appliedJobIds.has(j.id))

  const save = (payload: Partial<CandidateProfile>) =>
    updateMut.mutateAsync(payload).then(() => undefined)
  const savePerson = (payload: any) =>
    updatePersonMut.mutateAsync({ personId: cand.person, payload }).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/ats/candidates" className="text-muted-foreground hover:text-foreground hover:underline">← Candidates</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground">{cand.person_name}</h2>
            <div className="text-sm text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              {cand.person_headline && <span>{cand.person_headline}</span>}
              {!cand.person_headline && cand.person_current_title && (
                <span>{cand.person_current_title}{cand.person_current_company ? ` @ ${cand.person_current_company}` : ''}</span>
              )}
              {cand.person_email && <span>· {cand.person_email}</span>}
              {cand.person_linkedin_url && (
                <a href={cand.person_linkedin_url} target="_blank" rel="noopener noreferrer"
                  className="text-violet-600 hover:underline">LinkedIn ↗</a>
              )}
              <Link to={`/admin/hr/people/${cand.person}`} className="text-xs text-violet-600 hover:underline">
                HR profile →
              </Link>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
              <TouchChip lastTouchAt={cand.last_touch_at} />
              <FollowupChip nextFollowupAt={cand.next_followup_at} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            {cand.person_email && (
              <Button
                size="sm" variant="outline"
                title={`Email ${cand.person_email}`}
                onClick={() => {
                  // Open the user's mail client and stamp a touch so the
                  // attempt is recorded even if we can't see what they sent.
                  window.location.href = `mailto:${cand.person_email}`
                  logTouchMut.mutate({ kind: 'EMAIL', note: '', next_followup: null })
                }}
              >
                <Mail className="h-3 w-3 mr-1" /> Email
              </Button>
            )}
            <Button
              size="sm" variant="outline"
              onClick={() => setLogTouchOpen(true)}
            >
              <Clock className="h-3 w-3 mr-1" /> Log touch
            </Button>
            <Button
              size="sm"
              onClick={() => setConsiderOpen(true)}
              disabled={eligibleJobs.length === 0}
              title={
                eligibleJobs.length === 0
                  ? 'No open jobs the candidate isn\'t already applied to'
                  : `Add to one of ${eligibleJobs.length} open job${eligibleJobs.length === 1 ? '' : 's'}`
              }
            >
              <Plus className="h-3 w-3 mr-1" /> Add to job
            </Button>
            <InlineStatus
              value={cand.talent_pool_status}
              colorMap={TALENT_COLOR}
              options={[
                { value: 'NONE', label: 'Standard' },
                { value: 'SILVER', label: 'Silver medalist' },
                { value: 'GOLD', label: 'Gold (proactive nurture)' },
              ]}
              onSave={(v) => save({ talent_pool_status: v as CandidateProfile['talent_pool_status'] })}
            />
            {cand.is_archived && (
              <span className="text-xs px-2 py-0.5 rounded font-semibold bg-gray-200 text-gray-700">Archived</span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => save({ is_archived: !cand.is_archived })}>
                  {cand.is_archived ? 'Unarchive' : 'Archive'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => confirm(`profile for ${cand.person_name}`, () => deleteMut.mutate())}
                  className="text-destructive"
                >Delete profile</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Tags row */}
        <div className="mt-3">
          <TagsEditor
            allTags={allTags}
            value={cand.tags_detail}
            onChange={(ids) => save({ tags: ids })}
          />
        </div>
      </div>

      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['profile', 'applications', 'activity', 'notes', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-violet-600 text-violet-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'profile' && 'Profile'}
              {t === 'applications' && `Applications (${cand.total_applications_count})`}
              {t === 'activity' && 'Activity'}
              {t === 'notes' && 'Notes'}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'profile' && (
        <ProfileTab cand={cand} persons={persons} onSave={save} onSavePerson={savePerson} />
      )}
      {tab === 'applications' && (
        <ApplicationsTab
          candidate={cand}
          apps={apps}
          eligibleCount={eligibleJobs.length}
          onConsiderClick={() => setConsiderOpen(true)}
        />
      )}
      {tab === 'activity' && <ActivityTab personId={cand.person} />}
      {tab === 'notes' && <NotesTab cand={cand} onSave={save} />}
      {tab === 'files' && <FilesTab parent="ats.candidateprofile" parentId={candidateId} />}

      <ConsiderForJobDrawer
        open={considerOpen}
        onOpenChange={setConsiderOpen}
        candidate={cand}
        eligibleJobs={eligibleJobs}
      />

      <LogTouchDrawer
        open={logTouchOpen}
        onOpenChange={setLogTouchOpen}
        candidate={cand}
        onSubmit={(payload) => {
          logTouchMut.mutate(payload, {
            onSuccess: () => setLogTouchOpen(false),
          })
        }}
        isPending={logTouchMut.isPending}
      />
    </AdminLayout>
  )
}

// ─────────────────── Profile tab ───────────────────

type MatchingJob = {
  id: number
  code: string
  title: string
  department_name: string | null
  pipeline_id: number | null
  overlap_count: number
  overlap_tags: string[]
}

function ProfileTab({
  cand, persons, onSave, onSavePerson,
}: {
  cand: CandidateProfile
  persons: PersonLite[]
  onSave: (p: Partial<CandidateProfile>) => Promise<void>
  onSavePerson: (p: any) => Promise<void>
}) {
  const matchesQ = useQuery({
    queryKey: ['ats', 'candidate', cand.id, 'matching-jobs', cand.tags.join(',')],
    queryFn: async () =>
      (await api.get<MatchingJob[]>(`/ats/candidates/${cand.id}/matching-jobs/`)).data,
    // Only fire when the candidate has at least one tag — the endpoint
    // returns [] in that case anyway, but skipping the call keeps the panel
    // hidden cleanly.
    enabled: cand.tags.length > 0,
  })
  const matches = matchesQ.data || []

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {matches.length > 0 && (
        <Card title={`Suggested open jobs (${matches.length})`} className="lg:col-span-2">
          <p className="text-xs text-muted-foreground mb-3">
            Open jobs whose tags overlap this candidate's — sorted by overlap count.
          </p>
          <SuggestedJobsList matches={matches} candidateId={cand.id} />
        </Card>
      )}

      <Card title="Identity">
        <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Headline">
            <InlineText value={cand.person_headline}
              onSave={(v) => onSavePerson({ headline: v })}
              placeholder="One-liner — e.g. Senior backend engineer, Berlin" />
          </Row>
          <Row label="Current title">
            <InlineText value={cand.person_current_title}
              onSave={(v) => onSavePerson({ current_title: v })} />
          </Row>
          <Row label="Current company">
            <InlineText value={cand.person_current_company}
              onSave={(v) => onSavePerson({ current_company: v })} />
          </Row>
          <Row label="LinkedIn">
            <InlineText value={cand.person_linkedin_url}
              onSave={(v) => onSavePerson({ linkedin_url: v })}
              placeholder="https://linkedin.com/in/…" />
          </Row>
          <Row label="Email">
            <InlineText value={cand.person_email}
              onSave={(v) => onSavePerson({ personal_email: v })}
              placeholder="personal email" />
          </Row>
          <Row label="Mobile">
            <InlineText value={cand.person_mobile}
              onSave={(v) => onSavePerson({ mobile: v })} />
          </Row>
          <Row label="Country (ISO 2)">
            <InlineText value={cand.person_country_code}
              onSave={(v) => onSavePerson({ country_code: (v || '').toUpperCase() })}
              placeholder="RO, DE, US" />
          </Row>
        </dl>
      </Card>

      <Card title="Recruitment">
        <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Source">
            <InlineText value={cand.recruitment_source}
              onSave={(v) => onSave({ recruitment_source: v })}
              placeholder="LinkedIn / Referral / Direct / Job board" />
          </Row>
          <Row label="Owner">
            <InlineSelect
              value={cand.recruitment_owner ? String(cand.recruitment_owner) : ''}
              displayLabel={cand.owner_name || <Dash />}
              allowClear
              options={persons.map((p) => ({
                value: String(p.id), label: p.full_name, searchText: p.full_name,
              }))}
              onSave={(v) => onSave({ recruitment_owner: v ? Number(v) : null })}
              helper="The recruiter responsible for nurturing this candidate."
            />
          </Row>
          <Row label="Availability">
            <InlineText value={cand.availability}
              onSave={(v) => onSave({ availability: v })}
              placeholder="Available immediately / 30 days notice / In process elsewhere" />
          </Row>
          <Row label="Desired salary">
            <InlineNumber value={cand.desired_salary || ''}
              onSave={(v) => onSave({ desired_salary: v === '' ? null : v })}
              placeholder="Optional" />
          </Row>
          <Row label="Desired currency">
            <InlineText value={cand.desired_currency}
              onSave={(v) => onSave({ desired_currency: (v || '').toUpperCase().slice(0, 3) })}
              placeholder="EUR / USD / RON" />
          </Row>
        </dl>
      </Card>

      <Card title="Summary" className="lg:col-span-2">
        <InlineText value={cand.summary}
          multiline
          onSave={(v) => onSave({ summary: v })}
          placeholder="Recruiter-authored summary: background, why this person is interesting, what kinds of roles they're a fit for." />
      </Card>

      <Card title="GDPR & consent" className="lg:col-span-2">
        <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Consented at">
            <span>{cand.consent_at ? new Date(cand.consent_at).toLocaleString() : <Dash />}</span>
            {!cand.consent_at && (
              <button type="button"
                className="text-xs text-violet-600 hover:underline mt-1"
                onClick={() => onSave({ consent_at: new Date().toISOString() } as any)}>
                Record consent now
              </button>
            )}
          </Row>
          <Row label="Retain until">
            <InlineDate value={cand.retention_until}
              onSave={(v) => onSave({ retention_until: v })} />
          </Row>
        </dl>
      </Card>
    </div>
  )
}

// ─────────────────── Tags editor (header) ───────────────────

function TagsEditor({
  allTags, value, onChange,
}: {
  allTags: Tag[]
  value: Tag[]
  onChange: (tagIds: number[]) => void
}) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [input, setInput] = useState('')

  const createMut = useMutation({
    mutationFn: async (name: string) => (await api.post('/ats/tags/', { name })).data as Tag,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['ats', 'tags'] })
      // Attach the just-created tag.
      onChange([...value.map((t) => t.id), created.id])
      setInput(''); setAdding(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  function commitInput() {
    const raw = input.trim().toLowerCase().replace(/\s+/g, '-')
    if (!raw) return
    // Existing tag?
    const existing = allTags.find((t) => t.name === raw)
    if (existing) {
      if (!value.find((t) => t.id === existing.id)) {
        onChange([...value.map((t) => t.id), existing.id])
      }
      setInput(''); setAdding(false)
      return
    }
    createMut.mutate(raw)
  }

  function removeTag(id: number) {
    onChange(value.filter((t) => t.id !== id).map((t) => t.id))
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {value.map((t) => (
        <span key={t.id}
          className={`text-xs px-1.5 py-0.5 rounded font-semibold inline-flex items-center gap-1 ${TAG_CATEGORY_COLOR[t.category] || TAG_CATEGORY_COLOR['']}`}
          title={t.category || 'Uncategorised — admin can curate'}>
          {t.name}
          <button onClick={() => removeTag(t.id)} className="hover:opacity-70" aria-label={`Remove ${t.name}`}>
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {adding ? (
        <input
          autoFocus
          className="text-xs px-2 py-0.5 rounded border border-violet-300 outline-none bg-card"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onBlur={() => { if (input.trim()) commitInput(); else setAdding(false) }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commitInput() }
            if (e.key === 'Escape') { setInput(''); setAdding(false) }
          }}
          placeholder="tag-name"
          list="ats-tag-suggestions"
        />
      ) : (
        <button type="button" onClick={() => setAdding(true)}
          className="text-xs px-2 py-0.5 rounded border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-foreground/40">
          + tag
        </button>
      )}
      <datalist id="ats-tag-suggestions">
        {allTags.map((t) => <option key={t.id} value={t.name} />)}
      </datalist>
    </div>
  )
}

// ─────────────────── Applications tab ───────────────────

function ApplicationsTab({
  candidate, apps, eligibleCount, onConsiderClick,
}: {
  candidate: CandidateProfile
  apps: ApplicationLite[]
  eligibleCount: number
  onConsiderClick: () => void
}) {
  const navigate = useNavigate()
  // candidate is currently only used implicitly via apps prop; keep the
  // param so the call-site contract is obvious for future extensions.
  void candidate

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Every job this candidate has applied to or been considered for.
          {' '}
          <span className="font-medium">"Add to job"</span> drops them into the chosen stage of that job's pipeline.
        </p>
        <Button onClick={onConsiderClick} disabled={eligibleCount === 0}
          title={eligibleCount === 0 ? 'No open jobs the candidate isn\'t already applied to' : undefined}>
          <Plus className="h-3 w-3 mr-1" /> Add to job
        </Button>
      </div>

      {apps.length === 0 ? (
        <div className="bg-card rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
          No applications yet. Use <strong>Add to job</strong> (top right of the page) to drop this candidate into an open pipeline.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Job</Th>
                <Th>Stage</Th>
                <Th>Status</Th>
                <Th>Source</Th>
                <Th>Applied</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {apps.map((a) => (
                <tr
                  key={a.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/ats/applications/${a.id}`)}
                >
                  <Td>
                    <div className="font-mono text-xs">{a.job_code}</div>
                    <div className="text-sm font-medium">{a.job_title}</div>
                  </Td>
                  <Td className="text-xs">{a.stage_name}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${APP_STATUS_COLOR[a.status]}`}>
                      {a.status}
                    </span>
                  </Td>
                  <Td className="text-xs">{a.source || <Dash />}</Td>
                  <Td className="text-xs">{a.applied_at ? new Date(a.applied_at).toLocaleDateString() : <Dash />}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

type StageLite = {
  id: number; name: string; order: number; is_won: boolean; is_lost: boolean
}

function ConsiderForJobDrawer({
  open, onOpenChange, candidate, eligibleJobs,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  candidate: CandidateProfile
  eligibleJobs: JobLite[]
}) {
  const qc = useQueryClient()
  const [multiMode, setMultiMode] = useState(false)
  const [jobId, setJobId] = useState<number | null>(null)
  const [multiJobIds, setMultiJobIds] = useState<number[]>([])
  const [stageId, setStageId] = useState<number | null>(null)
  // Whether the recruiter manually picked a stage. If false, we keep
  // auto-syncing to "first non-terminal" as the job selection changes.
  const [stageManuallyChosen, setStageManuallyChosen] = useState(false)
  const [source, setSource] = useState(candidate.recruitment_source || 'Sourced')
  const [resumeUrl, setResumeUrl] = useState('')
  const [initialNote, setInitialNote] = useState('')

  useEffect(() => {
    if (open) {
      setMultiMode(false)
      setJobId(null)
      setMultiJobIds([])
      setStageId(null)
      setStageManuallyChosen(false)
      setSource(candidate.recruitment_source || 'Sourced')
      setResumeUrl('')
      setInitialNote('')
    }
  }, [open, candidate.recruitment_source])

  const pickedJob = jobId ? eligibleJobs.find((j) => j.id === jobId) ?? null : null

  // Stages for the picked Job's pipeline.
  const stagesQ = useQuery({
    queryKey: ['ats', 'stages', { pipeline: pickedJob?.pipeline ?? null }],
    queryFn: async () => {
      if (!pickedJob?.pipeline) return [] as StageLite[]
      return (await api.get<StageLite[]>(`/ats/stages/?pipeline=${pickedJob.pipeline}`)).data
    },
    enabled: !!pickedJob?.pipeline,
  })
  const stages = (stagesQ.data || []).slice().sort(
    (a, b) => a.order - b.order || a.id - b.id,
  )

  // Auto-default stage to the first non-terminal as soon as stages load,
  // unless the recruiter has already picked one manually.
  useEffect(() => {
    if (stageManuallyChosen) return
    if (stages.length === 0) { setStageId(null); return }
    const firstOpen = stages.find((s) => !s.is_won && !s.is_lost) ?? stages[0]
    setStageId(firstOpen.id)
  }, [stages, stageManuallyChosen])

  const createMut = useMutation({
    mutationFn: async () => {
      if (!jobId) throw new Error('Pick a job')
      if (!stageId) throw new Error('Pick a target stage')
      const created = (await api.post('/ats/applications/', {
        person: candidate.person,
        job: jobId,
        stage: stageId,
        source,
        resume_url: resumeUrl || undefined,
      })).data
      // Optional initial NOTE so the timeline opens with context.
      if (initialNote.trim()) {
        await api.post('/ats/activities/', {
          application: created.id,
          type: 'NOTE',
          body: initialNote.trim(),
        })
      }
      return created
    },
    onSuccess: () => {
      toast.success('Application created — candidate is in the pipeline')
      qc.invalidateQueries({ queryKey: ['ats', 'applications', { person: candidate.person }] })
      qc.invalidateQueries({ queryKey: ['ats', 'candidate', candidate.id] })
      qc.invalidateQueries({ queryKey: ['ats', 'candidates'] })
      qc.invalidateQueries({ queryKey: ['ats', 'kanban'] })
      onOpenChange(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  // Multi-mode: hits the bulk endpoint. Each job lands in its pipeline's
  // first non-terminal stage (no per-job stage picker in this mode).
  const bulkMut = useMutation({
    mutationFn: async () => {
      if (multiJobIds.length === 0) throw new Error('Pick at least one job')
      return (await api.post(`/ats/candidates/${candidate.id}/add-to-jobs/`, {
        jobs: multiJobIds,
        source,
        resume_url: resumeUrl || undefined,
      })).data as { created: number[]; skipped: { job: number; reason: string }[] }
    },
    onSuccess: (res) => {
      const c = res.created.length
      const s = res.skipped.length
      if (c > 0 && s === 0) {
        toast.success(`Added to ${c} job${c === 1 ? '' : 's'}`)
      } else if (c > 0 && s > 0) {
        toast.success(
          `Added to ${c} job${c === 1 ? '' : 's'} · ${s} skipped (${res.skipped[0].reason}${s > 1 ? ', …' : ''})`,
        )
      } else if (c === 0 && s > 0) {
        toast.error(`All ${s} skipped — ${res.skipped[0].reason}`)
      }
      qc.invalidateQueries({ queryKey: ['ats', 'applications', { person: candidate.person }] })
      qc.invalidateQueries({ queryKey: ['ats', 'candidate', candidate.id] })
      qc.invalidateQueries({ queryKey: ['ats', 'candidates'] })
      qc.invalidateQueries({ queryKey: ['ats', 'kanban'] })
      onOpenChange(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const stageOptions = stages.map((s) => ({
    value: String(s.id),
    label: `${s.name}${s.is_won ? ' (Won)' : s.is_lost ? ' (Lost)' : ''}`,
    hint: s.is_won ? 'terminal' : s.is_lost ? 'terminal' : undefined,
  }))

  function toggleMultiJob(id: number) {
    setMultiJobIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])
  }

  const isPending = multiMode ? bulkMut.isPending : createMut.isPending

  return (
    <RecordDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={`Add ${candidate.person_name} to ${multiMode ? 'multiple jobs' : 'a job'}`}
      description={
        multiMode
          ? "Drops the candidate into the first non-terminal stage of each picked job's pipeline. Stage isn't customisable in bulk mode."
          : "Creates an Application on the chosen job's pipeline. Default target is the first non-terminal stage; pick a different one if the candidate is already mid-process elsewhere."
      }
      onSubmit={() => (multiMode ? bulkMut.mutate() : createMut.mutate())}
      isPending={isPending}
      submitLabel={
        multiMode
          ? multiJobIds.length === 0
            ? 'Add to pipelines'
            : `Add to ${multiJobIds.length} pipeline${multiJobIds.length === 1 ? '' : 's'}`
          : 'Add to pipeline'
      }
    >
      <FieldGroup>
        {/* Mode toggle */}
        <div className="flex gap-1 text-xs bg-muted/40 rounded p-0.5 self-start">
          <button type="button"
            onClick={() => setMultiMode(false)}
            className={`px-3 py-1 rounded ${!multiMode ? 'bg-card shadow font-semibold' : 'text-muted-foreground'}`}>
            Single job (custom stage)
          </button>
          <button type="button"
            onClick={() => setMultiMode(true)}
            className={`px-3 py-1 rounded ${multiMode ? 'bg-card shadow font-semibold' : 'text-muted-foreground'}`}>
            Multiple jobs (default stage)
          </button>
        </div>

        {!multiMode && (
          <>
            <Field>
              <FieldLabel htmlFor="cfj-job">Job *</FieldLabel>
              <SearchableSelect
                id="cfj-job"
                value={jobId ? String(jobId) : NONE}
                onChange={(v) => {
                  setJobId(v === NONE ? null : Number(v))
                  // Re-enable auto-stage sync whenever the job changes — the old
                  // stage almost certainly belongs to a different pipeline.
                  setStageManuallyChosen(false)
                }}
                placeholder="— pick an open job —"
                options={[
                  { value: NONE, label: '— pick an open job —' },
                  ...eligibleJobs.map((j) => ({ value: String(j.id), label: j.title, hint: j.code })),
                ]}
              />
              <FieldDescription>
                Only OPEN jobs the candidate isn't already applied to.
              </FieldDescription>
            </Field>

            <Field>
              <FieldLabel htmlFor="cfj-stage">Target stage</FieldLabel>
              <SearchableSelect
                id="cfj-stage"
                value={stageId ? String(stageId) : NONE}
                onChange={(v) => {
                  setStageId(v === NONE ? null : Number(v))
                  setStageManuallyChosen(true)
                }}
                placeholder={
                  !pickedJob
                    ? 'Pick a job first'
                    : stagesQ.isLoading
                      ? 'Loading stages…'
                      : stages.length === 0
                        ? 'This pipeline has no stages'
                        : '— pick a stage —'
                }
                options={[
                  { value: NONE, label: '— pick a stage —' },
                  ...stageOptions,
                ]}
              />
              <FieldDescription>
                Defaults to the first non-terminal stage. Pick further along if the candidate has already cleared earlier steps externally.
              </FieldDescription>
            </Field>
          </>
        )}

        {multiMode && (
          <Field>
            <FieldLabel>Jobs *</FieldLabel>
            {eligibleJobs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No eligible open jobs.</p>
            ) : (
              <div className="rounded border border-border max-h-72 overflow-auto divide-y divide-border">
                {eligibleJobs.map((j) => {
                  const checked = multiJobIds.includes(j.id)
                  return (
                    <label key={j.id}
                      className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-muted/40 ${checked ? 'bg-violet-50' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMultiJob(j.id)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{j.title}</div>
                        <div className="text-xs text-muted-foreground font-mono">{j.code}</div>
                      </div>
                    </label>
                  )
                })}
              </div>
            )}
            <FieldDescription>
              {multiJobIds.length} selected. Each lands in its pipeline's first non-terminal stage.
            </FieldDescription>
          </Field>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field>
            <FieldLabel htmlFor="cfj-src">Source</FieldLabel>
            <Input id="cfj-src" value={source} onChange={(e) => setSource(e.target.value)}
              placeholder="Sourced / LinkedIn / Referral" />
            <FieldDescription>
              {multiMode ? 'Applied to every new application.' : 'Where this lead came from for this job.'}
            </FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor="cfj-resume">Resume URL</FieldLabel>
            <Input id="cfj-resume" type="url" value={resumeUrl}
              onChange={(e) => setResumeUrl(e.target.value)}
              placeholder="https://drive.google.com/…" />
            <FieldDescription>Optional. Attached to {multiMode ? 'every application' : 'this application'}.</FieldDescription>
          </Field>
        </div>

        {!multiMode && (
          <Field>
            <FieldLabel htmlFor="cfj-note">Initial note (optional)</FieldLabel>
            <Textarea id="cfj-note" rows={3} value={initialNote}
              onChange={(e) => setInitialNote(e.target.value)}
              placeholder="Context for the hiring panel — referral source, why you think they're a fit, prior conversations…" />
            <FieldDescription>
              Saved as the first NOTE in this application's timeline.
            </FieldDescription>
          </Field>
        )}
      </FieldGroup>
    </RecordDrawer>
  )
}

// ─────────────────── Activity tab (cross-application) ───────────────────

function ActivityTab({ personId }: { personId: number }) {
  // Fetch all applications for this person, then all activities for those apps,
  // and merge into one timeline.
  const appsQ = useQuery({
    queryKey: ['ats', 'applications', { person: personId }],
    queryFn: async () =>
      (await api.get<ApplicationLite[]>(`/ats/applications/?person=${personId}`)).data,
  })
  const apps = appsQ.data || []
  const activityQs = useQuery({
    queryKey: ['ats', 'activities', 'cross', personId, apps.map((a) => a.id).sort().join(',')],
    queryFn: async () => {
      if (apps.length === 0) return []
      const results: Activity[] = []
      for (const a of apps) {
        const list = (await api.get<Activity[]>(`/ats/activities/?application=${a.id}`)).data
        for (const x of list) results.push(x)
      }
      results.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      return results
    },
    enabled: !appsQ.isLoading,
  })

  if (appsQ.isLoading || activityQs.isLoading) {
    return <p className="text-muted-foreground">Loading…</p>
  }
  const activities = activityQs.data || []
  if (activities.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
        No activity yet across this candidate's applications.
      </div>
    )
  }
  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <ul className="divide-y divide-border">
        {activities.map((a) => {
          const app = apps.find((x) => x.id === a.application)
          return (
            <li key={a.id} className="px-4 py-3">
              <div className="flex items-start gap-3">
                <span className={`text-xs px-2 py-0.5 rounded font-semibold whitespace-nowrap ${ACTIVITY_COLOR[a.type] || ACTIVITY_COLOR.NOTE}`}>
                  {a.type}
                </span>
                <div className="flex-1 min-w-0">
                  {app && (
                    <div className="text-xs text-muted-foreground mb-0.5">
                      on{' '}
                      <Link to={`/admin/ats/applications/${app.id}`} className="text-violet-600 hover:underline">
                        {app.job_code} {app.job_title}
                      </Link>
                    </div>
                  )}
                  {a.body && <div className="text-sm whitespace-pre-wrap">{a.body}</div>}
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {a.performed_by_username || 'system'} · {new Date(a.timestamp).toLocaleString()}
                    {a.automatic && <span className="ml-2 italic">auto</span>}
                  </div>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ─────────────────── Touch / follow-up chips ───────────────────

function TouchChip({ lastTouchAt }: { lastTouchAt: string | null }) {
  if (!lastTouchAt) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
        <Clock className="h-3 w-3" /> No touches yet
      </span>
    )
  }
  const d = new Date(lastTouchAt)
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000)
  const stale = days > 30
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${
      stale ? 'bg-amber-100 text-amber-800' : 'bg-muted text-muted-foreground'
    }`}>
      <Clock className="h-3 w-3" />
      Last touch: {days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`}
    </span>
  )
}

function FollowupChip({ nextFollowupAt }: { nextFollowupAt: string | null }) {
  if (!nextFollowupAt) return null
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const d = new Date(nextFollowupAt); d.setHours(0, 0, 0, 0)
  const days = Math.round((d.getTime() - today.getTime()) / 86_400_000)
  const overdue = days < 0
  const dueToday = days === 0
  const label = overdue
    ? `Overdue by ${Math.abs(days)}d`
    : dueToday
      ? 'Due today'
      : `Due in ${days}d`
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${
      overdue ? 'bg-red-100 text-red-700'
        : dueToday ? 'bg-amber-100 text-amber-800'
          : 'bg-violet-100 text-violet-700'
    }`}>
      <CalendarClock className="h-3 w-3" />
      {label} · {d.toLocaleDateString()}
    </span>
  )
}

// ─────────────────── Log touch drawer ───────────────────

const TOUCH_KINDS = [
  { value: 'EMAIL',    label: 'Email' },
  { value: 'CALL',     label: 'Call' },
  { value: 'MEETING',  label: 'Meeting' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'OTHER',    label: 'Other' },
]

function LogTouchDrawer({
  open, onOpenChange, candidate, onSubmit, isPending,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  candidate: CandidateProfile
  onSubmit: (payload: { kind: string; note: string; next_followup: string | null }) => void
  isPending: boolean
}) {
  const [kind, setKind] = useState('EMAIL')
  const [note, setNote] = useState('')
  const [followup, setFollowup] = useState<string>('')

  useEffect(() => {
    if (open) {
      setKind('EMAIL')
      setNote('')
      setFollowup('')
    }
  }, [open])

  return (
    <RecordDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={`Log a touch with ${candidate.person_name}`}
      description="Stamps last touch = now and (optionally) sets a follow-up date. The note is appended to the most recent application's timeline."
      onSubmit={() => onSubmit({
        kind, note,
        next_followup: followup || null,
      })}
      isPending={isPending}
      submitLabel="Log touch"
    >
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="lt-kind">Channel</FieldLabel>
          <SearchableSelect
            id="lt-kind"
            value={kind}
            onChange={(v) => setKind(v)}
            placeholder="—"
            options={TOUCH_KINDS}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="lt-note">Note</FieldLabel>
          <Textarea id="lt-note" rows={3} value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What you said, what they said, next step…" />
          <FieldDescription>Appended to the candidate's latest application timeline as a NOTE.</FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor="lt-followup">Follow up on (optional)</FieldLabel>
          <Input id="lt-followup" type="date" value={followup}
            onChange={(e) => setFollowup(e.target.value)} />
          <FieldDescription>Stamps next_followup_at — overdue items surface in the candidate list.</FieldDescription>
        </Field>
      </FieldGroup>
    </RecordDrawer>
  )
}

// ─────────────────── Notes tab ───────────────────

function NotesTab({
  cand, onSave,
}: { cand: CandidateProfile; onSave: (p: Partial<CandidateProfile>) => Promise<void> }) {
  return (
    <Card title="Recruiter notes (cross-application)">
      <p className="text-xs text-muted-foreground mb-3">
        Long-form notes about the candidate as a person. Per-application notes still live on each Application's detail page.
      </p>
      <InlineText
        value={cand.notes_recruitment}
        multiline
        onSave={(v) => onSave({ notes_recruitment: v })}
        placeholder="Background, preferences, prior conversations, follow-ups…"
      />
    </Card>
  )
}

// ─────────────────── Suggested jobs list ───────────────────

function SuggestedJobsList({
  matches, candidateId,
}: {
  matches: MatchingJob[]
  candidateId: number
}) {
  const navigate = useNavigate()
  return (
    <ul className="divide-y divide-border">
      {matches.map((m) => (
        <li key={m.id} className="py-2 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{m.code}</span>
              <Link
                to={`/admin/ats/jobs/${m.id}`}
                className="text-sm font-medium hover:underline truncate"
              >
                {m.title}
              </Link>
              {m.department_name && (
                <span className="text-xs text-muted-foreground">· {m.department_name}</span>
              )}
            </div>
            <div className="mt-0.5 flex flex-wrap gap-1">
              {m.overlap_tags.map((t) => (
                <span key={t} className="text-xs px-1.5 py-0.5 rounded font-semibold bg-violet-100 text-violet-700">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {m.overlap_count} tag match{m.overlap_count === 1 ? '' : 'es'}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/admin/ats/jobs/${m.id}`)}
              title={`Open ${m.title}`}
            >
              Open job
            </Button>
          </div>
        </li>
      ))}
      <li className="pt-2 text-xs text-muted-foreground">
        Tip: use <strong>Add to job</strong> at the top of the page to drop the candidate into one of these pipelines.
        <span className="opacity-60 ml-1">(candidate {candidateId})</span>
      </li>
    </ul>
  )
}

// ─────────────────── Helpers ───────────────────

function Card({ title, children, className = '' }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={`bg-card rounded-lg shadow p-6 ${className}`}>
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{title}</h3>
      {children}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

function Th({ children }: { children: ReactNode }) {
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
      {children}
    </th>
  )
}

function Td({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>
}
